/*
  KS speed runtime — 13 Sep 2026
  ------------------------------------------------------------------
  Three jobs, none of which change what the page looks like:

  1. swap()          images rendered with data-ks-src/data-ks-srcset get their real
                     sources only when they are about to be seen. Used for below-the-fold
                     product cards and for gallery photos that sit off-screen *sideways*
                     (native lazy-loading treats those as visible, so the browser
                     downloads every slide during the initial load).
  2. carousels       one-slide-ahead loading inside a horizontal scroller, plus a
                     swap-everything on the first interaction (the zoom viewer reads
                     <img> sources when it opens).
  3. trustpilot      the widget bootstrap is only fetched once a TrustBox approaches.

  If IntersectionObserver is missing, everything is swapped immediately, so the
  page degrades to exactly the behaviour it had before.
*/
/*
  KSAccordion — the three jQuery methods the theme actually used (.slideUp, .slideDown,
  .slideToggle plus .next(selector)), so jQuery itself no longer has to be downloaded
  from a third origin on every page. Timing and easing match jQuery's defaults closely
  enough that the panels feel the same.
*/
window.KSAccordion = (function () {
  'use strict';

  var MS = 300;

  function next(el, cls) {
    var n = el && el.nextElementSibling;
    return n && n.classList.contains(cls) ? n : null;
  }

  function hidden(el) {
    return !el || el.offsetParent === null || getComputedStyle(el).display === 'none';
  }

  function clear(el) {
    el.style.transition = '';
    el.style.height = '';
    el.style.overflow = '';
    el.style.paddingTop = '';
    el.style.paddingBottom = '';
    el.style.marginTop = '';
    el.style.marginBottom = '';
  }

  function animate(el, down, done) {
    if (!el) return;
    if (el._ksAnim) clearTimeout(el._ksAnim);

    if (down) {
      el.style.removeProperty('display');
      if (getComputedStyle(el).display === 'none') el.style.display = 'block';
    }

    var target = el.scrollHeight;
    var cs = getComputedStyle(el);
    var pt = cs.paddingTop, pb = cs.paddingBottom, mt = cs.marginTop, mb = cs.marginBottom;

    el.style.overflow = 'hidden';
    el.style.transition = 'none';
    if (down) {
      el.style.height = '0px';
      el.style.paddingTop = '0px'; el.style.paddingBottom = '0px';
      el.style.marginTop = '0px'; el.style.marginBottom = '0px';
    } else {
      el.style.height = target + 'px';
    }
    // force a reflow so the starting values are committed before the transition
    void el.offsetHeight;

    el.style.transition = 'height ' + MS + 'ms ease-in-out, padding ' + MS + 'ms ease-in-out, margin ' + MS + 'ms ease-in-out';
    if (down) {
      el.style.height = target + 'px';
      el.style.paddingTop = pt; el.style.paddingBottom = pb;
      el.style.marginTop = mt; el.style.marginBottom = mb;
    } else {
      el.style.height = '0px';
      el.style.paddingTop = '0px'; el.style.paddingBottom = '0px';
      el.style.marginTop = '0px'; el.style.marginBottom = '0px';
    }

    el._ksAnim = setTimeout(function () {
      el._ksAnim = null;
      clear(el);
      if (!down) el.style.display = 'none';
      if (done) done();
    }, MS);
  }

  return {
    next: next,
    hidden: hidden,
    // jQuery's slideDown on an already-visible element is a no-op; keep that.
    show: function (el) { if (el && hidden(el)) animate(el, true); },
    slideDown: function (el) { if (el && hidden(el)) animate(el, true); },
    slideUp: function (el) { if (el && !hidden(el)) animate(el, false); },
    slideToggle: function (el) { if (el) animate(el, hidden(el)); }
  };
})();

(function () {
  'use strict';

  var IO = window.IntersectionObserver;
  var d = document;

  /* ---------------------------------------------------------------- swap */

  function swap(img) {
    if (!img || !img.dataset) return;
    var src = img.dataset.ksSrc;
    var srcset = img.dataset.ksSrcset;
    if (!src && !srcset) return;
    delete img.dataset.ksSrc;
    delete img.dataset.ksSrcset;
    if (img.dataset.ksSizes) {
      img.setAttribute('sizes', img.dataset.ksSizes);
      delete img.dataset.ksSizes;
    }
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    img.setAttribute('loading', 'eager');
  }

  function swapIn(root) {
    if (!root) return;
    if (root.tagName === 'IMG') swap(root);
    var list = root.querySelectorAll ? root.querySelectorAll('img[data-ks-src],img[data-ks-srcset]') : [];
    for (var i = 0; i < list.length; i++) swap(list[i]);
  }

  function swapEverything() {
    swapIn(d.body || d.documentElement);
  }

  if (!IO) {
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', swapEverything);
    else swapEverything();
    return;
  }

  /* ------------------------------------------------- vertical approach */

  // Containers that should load when the page scrolls near them.
  // data-ks-defer="far" gets a much bigger margin: Safari's own lazy loading
  // starts too late for large editorial images and they visibly pop in.
  var near = new IO(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      near.unobserve(entries[i].target);
      swapIn(entries[i].target);
    }
  }, { rootMargin: '500px 0px' });

  var far = new IO(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      far.unobserve(entries[i].target);
      swapIn(entries[i].target);
    }
  }, { rootMargin: '1500px 0px' });

  var observed = 'ksObserved';

  function observeDefer(root) {
    var list = (root || d).querySelectorAll('[data-ks-defer]');
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.dataset[observed]) continue;
      el.dataset[observed] = '1';
      (el.getAttribute('data-ks-defer') === 'far' ? far : near).observe(el);
    }
  }

  /* ----------------------------------------------- horizontal scrollers */

  // One slide ahead inside the scroller itself, so a swipe never lands on a
  // placeholder, and the images for slides the visitor never reaches are
  // never requested at all.
  // Swap the slides within ~1.5 scroller-widths of where the scroller currently sits, so
  // the next swipe always lands on a real photo and the ones further along stay unfetched.
  function swapNearScroll(scroller) {
    var reach = scroller.clientWidth * 1.5;
    var from = scroller.scrollLeft - reach;
    var to = scroller.scrollLeft + scroller.clientWidth + reach;
    var slides = scroller.children;
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i];
      if (s.offsetLeft + s.offsetWidth >= from && s.offsetLeft <= to) swapIn(s);
    }
  }

  var scrollersByRoot = [];

  function initScroller(scroller) {
    if (scroller.dataset.ksScroller) return;
    scroller.dataset.ksScroller = '1';
    scrollersByRoot.push(scroller);

    swapNearScroll(scroller);

    var queued = false;
    scroller.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        swapNearScroll(scroller);
      });
    }, { passive: true });

    // First real interaction: load the lot. The zoom viewer reads <img> sources when it
    // opens and its button sits outside the scroller, so listen on the whole gallery.
    var root = scroller.closest('product-gallery') || scroller.parentElement || scroller;
    var events = ['pointerdown', 'touchstart', 'keydown', 'wheel'];
    var done = false;
    function all() {
      if (done) return;
      done = true;
      swapIn(scroller);
      events.forEach(function (t) { root.removeEventListener(t, all, true); });
    }
    events.forEach(function (t) {
      root.addEventListener(t, all, { capture: true, passive: true });
    });
  }

  function observeScrollers(root) {
    var list = (root || d).querySelectorAll('[data-ks-defer-x]');
    for (var i = 0; i < list.length; i++) initScroller(list[i]);
  }

  // A variant change can scroll the gallery to a different photo. Load what it lands on
  // rather than the whole gallery — the theme fires this event on page load too, and
  // swapping everything there would undo the point of deferring in the first place.
  d.addEventListener('variant:change', function () {
    requestAnimationFrame(function () {
      for (var i = 0; i < scrollersByRoot.length; i++) swapNearScroll(scrollersByRoot[i]);
    });
  });

  /* ----------------------------------------------------- trustpilot */

  // The bootstrap is ~8 KB plus a new origin, and every TrustBox it finds becomes a full
  // iframe document. None of that is needed until the visitor scrolls a review block into
  // view, so the theme no longer ships the script tag and we fetch it on approach instead.
  //
  // The Trustpilot Shopify app also injects this bootstrap through its own script tag; if
  // that has already happened there is nothing left for us to do.
  function initTrustpilot() {
    var boxes = d.querySelectorAll('.trustpilot-widget');
    if (!boxes.length) return;
    var loaded = false;

    function load() {
      if (loaded) return;
      loaded = true;
      tpIO.disconnect();
      if (window.Trustpilot || d.querySelector('script[src*="tp.widget.bootstrap"]')) return;
      var s = d.createElement('script');
      s.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
      s.async = true;
      d.head.appendChild(s);
    }

    var tpIO = new IO(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { load(); return; }
      }
    }, { rootMargin: '400px 0px' });

    for (var i = 0; i < boxes.length; i++) tpIO.observe(boxes[i]);
  }

  /* ------------------------------------------------------------ start */

  function scan(root) {
    observeDefer(root);
    observeScrollers(root);
  }

  function start() {
    scan(d);
    initTrustpilot();

    // Sections re-render (filters, cart drawer, quick buy, the Section
    // Rendering API) — pick up anything new, batched to one frame.
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        scan(d);
      });
    }).observe(d.documentElement, { childList: true, subtree: true });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
})();
